import { permissionsList } from "components/Common/constants"
import {
  COUNTRY_PERMISSION_CATEGORY_ID,
  LEGACY_PLAN_BRAND_PERMISSION_CATEGORY_ID,
  PLAN_FEATURE_TO_PERMISSION_CATEGORIES,
  TENANT_COUNTRY_MUTATION_PERMISSION_IDS,
  ZONE_PERMISSION_CATEGORY_ID,
  normalizePlanFeatureKey,
} from "constants/planPermissionMap"

/** Sub-permissions that refine a parent action but are not required to keep the parent enabled. */
export const OPTIONAL_PLAN_SUB_PERMISSION_IDS = new Set([
  "link_discount_with_pos",
  "link_menu_with_pos",
  "enable_erp_integration",
  "customize_notifications",
  "customize_notifications_ios",
  "generate_order_qr",
  "city_zones_map",
  "city_map",
])

/** Optional subs that still appear on the role form whenever the parent is granted. */
export const ALWAYS_ASSIGNABLE_OPTIONAL_SUB_IDS = new Set(["city_map"])

export const visibleCatalogSubs = (catalogSubs = [], allowedSubs = null) => {
  const subs = catalogSubs || []
  if (allowedSubs && allowedSubs.size > 0) {
    return subs.filter(
      sub =>
        allowedSubs.has(String(sub.id)) ||
        ALWAYS_ASSIGNABLE_OPTIONAL_SUB_IDS.has(String(sub.id))
    )
  }
  return subs.filter(
    sub =>
      !OPTIONAL_PLAN_SUB_PERMISSION_IDS.has(String(sub.id)) ||
      ALWAYS_ASSIGNABLE_OPTIONAL_SUB_IDS.has(String(sub.id))
  )
}

/** Foodics extras — opt-in when POS Settings is enabled.
 *  Menu sync / branch sync use existing POS categories 30 / 29.
 */
const FOODICS_FEATURE_CATEGORY_ID = 58
const FOODICS_OPT_IN_FEATURE_IDS = new Set([
  "foodics_combos_sync_3",
  "foodics_discount_link_3",
  "foodics_order_calculator_3",
  "foodics_charges_sync_3",
])

export const isTenantCountryMutationPermission = (categoryId, permissionId) =>
  Number(categoryId) === COUNTRY_PERMISSION_CATEGORY_ID &&
  TENANT_COUNTRY_MUTATION_PERMISSION_IDS.includes(String(permissionId))

/** FNB/B2C under Menus — replaced by Assigned Brands on the plan. */
export const isLegacyPlanBrandPermissionCategory = categoryId =>
  Number(categoryId) === LEGACY_PLAN_BRAND_PERMISSION_CATEGORY_ID

const resolvePermissionEnabledForCategory = (
  categoryId,
  permissionId,
  allEnabled
) => {
  if (!allEnabled) return false
  if (isTenantCountryMutationPermission(categoryId, permissionId)) return false
  if (
    Number(categoryId) === FOODICS_FEATURE_CATEGORY_ID &&
    FOODICS_OPT_IN_FEATURE_IDS.has(String(permissionId))
  ) {
    return false
  }
  return true
}

const LEGACY_ZONE_PERMISSION_CATEGORY_ID = 42

/** Old bundled branch fulfillment subs → granular plan/role ids. */
const LEGACY_BRANCH_FULFILLMENT_SUB_MAP = {
  enable_pickup_curbside: ["enable_pickup", "enable_delivery", "enable_curbside"],
  enable_dine_drive_thru: ["enable_drive_thru", "enable_dine_in"],
}

const expandLegacyBranchFulfillmentSubIds = subSet => {
  if (!(subSet instanceof Set) || subSet.size === 0) return
  Object.entries(LEGACY_BRANCH_FULFILLMENT_SUB_MAP).forEach(
    ([legacyId, splitIds]) => {
      if (!subSet.has(legacyId)) return
      subSet.delete(legacyId)
      splitIds.forEach(id => subSet.add(id))
    }
  )
}

const isZonePermissionGrantId = permissionId =>
  String(permissionId || "").startsWith("zone_")

const mergeGrantPermissionMaps = (targetMap, sourceMap) => {
  sourceMap.forEach((subSet, permId) => {
    const existing = targetMap.get(permId) || new Set()
    subSet.forEach(subId => existing.add(subId))
    targetMap.set(permId, existing)
  })
}

const normalizeLegacyZoneGrantCategories = byCategory => {
  const legacyMap = byCategory.get(LEGACY_ZONE_PERMISSION_CATEGORY_ID)
  if (!legacyMap) return

  const zoneOnly = new Map()
  const nonZone = new Map()
  legacyMap.forEach((subSet, permId) => {
    if (isZonePermissionGrantId(permId)) {
      zoneOnly.set(permId, subSet)
    } else {
      nonZone.set(permId, subSet)
    }
  })

  if (zoneOnly.size === 0) return

  const canonicalMap = byCategory.get(ZONE_PERMISSION_CATEGORY_ID) || new Map()
  mergeGrantPermissionMaps(canonicalMap, zoneOnly)
  byCategory.set(ZONE_PERMISSION_CATEGORY_ID, canonicalMap)

  if (nonZone.size > 0) {
    byCategory.set(LEGACY_ZONE_PERMISSION_CATEGORY_ID, nonZone)
  } else {
    byCategory.delete(LEGACY_ZONE_PERMISSION_CATEGORY_ID)
  }
}

export const getCategoryIdsForModule = moduleId =>
  PLAN_FEATURE_TO_PERMISSION_CATEGORIES[moduleId] || []

export const getPermissionGroupByCategoryId = categoryId =>
  permissionsList.find(group => Number(group.id) === Number(categoryId))

export const parentGrantKey = (categoryId, permissionId) =>
  `${Number(categoryId)}::${permissionId}`

export const subGrantKey = (categoryId, parentPermissionId, subPermissionId) =>
  `${Number(categoryId)}::${parentPermissionId}::${subPermissionId}`

const toKeySet = keys => new Set(Array.isArray(keys) ? keys : [])

export const buildFullGrantKeysForCategories = categoryIds => {
  const keys = new Set()
  ;(categoryIds || []).forEach(categoryId => {
    const group = getPermissionGroupByCategoryId(categoryId)
    if (!group) return
    ;(group.permissions || []).forEach(permission => {
      if (
        Number(categoryId) === FOODICS_FEATURE_CATEGORY_ID &&
        FOODICS_OPT_IN_FEATURE_IDS.has(String(permission.id))
      ) {
        return
      }
      if (isTenantCountryMutationPermission(categoryId, permission.id)) {
        return
      }
      keys.add(parentGrantKey(categoryId, permission.id))
      ;(permission.subPermissions || []).forEach(sub => {
        keys.add(subGrantKey(categoryId, permission.id, sub.id))
      })
    })
  })
  return Array.from(keys)
}

export const buildFullGrantsForCategories = categoryIds =>
  grantsFromKeys(buildFullGrantKeysForCategories(categoryIds))

export const sanitizePermissionGrants = rawGrants => {
  if (!Array.isArray(rawGrants)) return []
  const byCategory = new Map()

  rawGrants.forEach(entry => {
    const categoryId = Number(entry?.categoryId)
    if (Number.isNaN(categoryId) || isLegacyPlanBrandPermissionCategory(categoryId)) return
    const permissionMap = byCategory.get(categoryId) || new Map()
    ;(entry.permissions || []).forEach(permission => {
      const permId = String(permission?.id || permission?.permissionId || "").trim()
      if (!permId) return
      if (isTenantCountryMutationPermission(categoryId, permId)) return
      const existing = permissionMap.get(permId) || new Set()
      ;(permission.subPermissionIds || []).forEach(subId => {
        const normalized = String(subId || "").trim()
        if (normalized) existing.add(normalized)
      })
      if (permId === "branchLocations_3") {
        expandLegacyBranchFulfillmentSubIds(existing)
      }
      permissionMap.set(permId, existing)
    })
    byCategory.set(categoryId, permissionMap)
  })

  normalizeLegacyZoneGrantCategories(byCategory)

  // Do not auto-inject Delivery Settings (59) from Order Cancel (50).
  // That coupling made deselecting Delivery Settings impossible to persist.
  // Legacy plans are backfilled once via dbMigration.

  return Array.from(byCategory.entries())
    .sort(([a], [b]) => a - b)
    .map(([categoryId, permissionMap]) => ({
      categoryId,
      permissions: Array.from(permissionMap.entries()).map(([id, subSet]) => ({
        id,
        subPermissionIds: Array.from(subSet),
      })),
    }))
    .filter(entry => entry.permissions.length > 0)
}

export const keysFromGrantsExact = grants => {
  const keys = []
  sanitizePermissionGrants(grants).forEach(entry => {
    ;(entry.permissions || []).forEach(permission => {
      keys.push(parentGrantKey(entry.categoryId, permission.id))
      ;(permission.subPermissionIds || []).forEach(subId => {
        keys.push(subGrantKey(entry.categoryId, permission.id, subId))
      })
    })
  })
  return keys
}

export const grantsFromKeys = keys => {
  const keySet = toKeySet(keys)
  const byCategory = new Map()

  permissionsList.forEach(group => {
    const categoryId = Number(group.id)
    ;(group.permissions || []).forEach(permission => {
      if (!keySet.has(parentGrantKey(categoryId, permission.id))) return
      const subPermissionIds = (permission.subPermissions || [])
        .map(sub => sub.id)
        .filter(subId => keySet.has(subGrantKey(categoryId, permission.id, subId)))
      if (!byCategory.has(categoryId)) byCategory.set(categoryId, [])
      byCategory.get(categoryId).push({
        id: permission.id,
        subPermissionIds,
      })
    })
  })

  return Array.from(byCategory.entries())
    .sort(([a], [b]) => a - b)
    .map(([categoryId, permissions]) => ({ categoryId, permissions }))
}

export const deriveCategoryIdsFromPlan = plan => {
  if (Array.isArray(plan?.basePermissions) && plan.basePermissions.length > 0) {
    return plan.basePermissions.map(Number).filter(id => !Number.isNaN(id))
  }
  const keys = (plan?.features || [])
    .map(feature =>
      normalizePlanFeatureKey(
        feature?.name || feature?.id || feature?.moduleId || feature?.code || ""
      )
    )
    .filter(Boolean)
  const derived = new Set()
  keys.forEach(moduleKey => {
    ;(PLAN_FEATURE_TO_PERMISSION_CATEGORIES[moduleKey] || []).forEach(categoryId =>
      derived.add(categoryId)
    )
  })
  return Array.from(derived)
}

export const inferPermissionGrantsFromPlan = plan => {
  if (Array.isArray(plan?.permissionGrants) && plan.permissionGrants.length > 0) {
    return sanitizePermissionGrants(plan.permissionGrants)
  }
  return buildFullGrantsForCategories(deriveCategoryIdsFromPlan(plan))
}

export const inferGrantKeysFromPlan = plan =>
  keysFromGrantsExact(inferPermissionGrantsFromPlan(plan))

export const isParentPermissionGranted = (grantKeys, categoryId, permissionId) =>
  toKeySet(grantKeys).has(parentGrantKey(categoryId, permissionId))

export const isSubPermissionGranted = (
  grantKeys,
  categoryId,
  parentPermissionId,
  subPermissionId
) =>
  toKeySet(grantKeys).has(
    subGrantKey(categoryId, parentPermissionId, subPermissionId)
  )

export const syncGrantKeysForModule = (grantKeys, moduleId, enabled) => {
  const categoryIds = getCategoryIdsForModule(moduleId)
  const moduleKeys = new Set(buildFullGrantKeysForCategories(categoryIds))
  const next = new Set(grantKeys || [])
  if (enabled) {
    moduleKeys.forEach(key => next.add(key))
  } else {
    moduleKeys.forEach(key => next.delete(key))
  }
  return Array.from(next)
}

export const toggleParentPermissionGrant = (grantKeys, categoryId, permissionId, enabled) => {
  const group = getPermissionGroupByCategoryId(categoryId)
  const permission = (group?.permissions || []).find(item => item.id === permissionId)
  const next = new Set(grantKeys || [])
  const parentKey = parentGrantKey(categoryId, permissionId)

  if (enabled) {
    next.add(parentKey)
    ;(permission?.subPermissions || []).forEach(sub => {
      next.add(subGrantKey(categoryId, permissionId, sub.id))
    })
  } else {
    next.delete(parentKey)
    ;(permission?.subPermissions || []).forEach(sub => {
      next.delete(subGrantKey(categoryId, permissionId, sub.id))
    })
  }

  return Array.from(next)
}

export const toggleSubPermissionGrant = (
  grantKeys,
  categoryId,
  parentPermissionId,
  subPermissionId,
  enabled
) => {
  const next = new Set(grantKeys || [])
  const parentKey = parentGrantKey(categoryId, parentPermissionId)
  const subKey = subGrantKey(categoryId, parentPermissionId, subPermissionId)

  if (enabled) {
    next.add(parentKey)
    next.add(subKey)
  } else {
    next.delete(subKey)
  }

  return Array.from(next)
}

export const toggleCategoryAllGrants = (grantKeys, categoryId, selectAll) => {
  const categoryKeys = new Set(buildFullGrantKeysForCategories([categoryId]))
  const next = new Set(grantKeys || [])
  if (selectAll) {
    categoryKeys.forEach(key => next.add(key))
  } else {
    categoryKeys.forEach(key => next.delete(key))
  }
  return Array.from(next)
}

export const syncGrantsForModule = (grants, moduleId, enabled) => {
  const categoryIds = getCategoryIdsForModule(moduleId)
  const withoutModule = (grants || []).filter(
    entry => !categoryIds.includes(Number(entry.categoryId))
  )
  if (!enabled) return withoutModule
  const existingIds = new Set(withoutModule.map(entry => Number(entry.categoryId)))
  const toAdd = categoryIds.filter(categoryId => !existingIds.has(Number(categoryId)))
  return [...withoutModule, ...buildFullGrantsForCategories(toAdd)]
}

export const getPermissionLabel = (permission, language) =>
  language === "ar" || language === "sa"
    ? permission.labelAr || permission.label
    : permission.label || permission.labelAr

export const buildCategorySelection = (categoryId, allEnabled) => {
  const group = getPermissionGroupByCategoryId(categoryId)
  if (!group) return null
  return {
    categoryId: Number(categoryId),
    permissions: (group.permissions || []).map(permission => ({
      id: permission.id,
      isEnabled: resolvePermissionEnabledForCategory(
        categoryId,
        permission.id,
        allEnabled
      ),
      subPermissions: (permission.subPermissions || []).map(sub => ({
        id: sub.id,
        isEnabled: resolvePermissionEnabledForCategory(
          categoryId,
          permission.id,
          allEnabled
        ),
      })),
    })),
  }
}

export const buildCategorySelectionFromGrantKeys = (categoryId, grantKeys) => {
  const group = getPermissionGroupByCategoryId(categoryId)
  if (!group) return null
  const keySet = toKeySet(grantKeys)
  return {
    categoryId: Number(categoryId),
    permissions: (group.permissions || []).map(permission => {
      const parentEnabled = keySet.has(parentGrantKey(categoryId, permission.id))
      const subs = permission.subPermissions || []
      const hasExplicitSubGrant = subs.some(sub =>
        keySet.has(subGrantKey(categoryId, permission.id, sub.id))
      )
      return {
        id: permission.id,
        isEnabled: parentEnabled,
        subPermissions: subs.map(sub => ({
          id: sub.id,
          isEnabled:
            parentEnabled &&
            (keySet.has(subGrantKey(categoryId, permission.id, sub.id)) ||
              (!hasExplicitSubGrant &&
                !OPTIONAL_PLAN_SUB_PERMISSION_IDS.has(String(sub.id)))),
        })),
      }
    }),
  }
}

/** Plain object keyed by categoryId — React state updates reliably with object spread. */
export const buildSelectionsForCategories = (categoryIds, allEnabled) => {
  const out = {}
  ;(categoryIds || []).forEach(categoryId => {
    const selection = buildCategorySelection(categoryId, allEnabled)
    if (selection) out[Number(categoryId)] = selection
  })
  return out
}

export const buildSelectionsFromGrantKeys = (categoryIds, grantKeys) => {
  const out = {}
  ;(categoryIds || []).forEach(categoryId => {
    const selection = buildCategorySelectionFromGrantKeys(categoryId, grantKeys)
    if (selection) out[Number(categoryId)] = selection
  })
  return out
}

export const buildSelectionsMapForCategories = buildSelectionsForCategories
export const buildSelectionsMapFromGrantKeys = buildSelectionsFromGrantKeys

const samePermissionId = (a, b) => String(a || "") === String(b || "")

/** Ensure selection includes every permission from the catalog (needed for reliable toggles). */
export const mergeCategorySelectionWithCatalog = (categoryId, partial) => {
  const group = getPermissionGroupByCategoryId(categoryId)
  if (!group) return partial || null
  const saved = partial?.permissions || []
  return {
    categoryId: Number(categoryId),
    permissions: (group.permissions || []).map(permission => {
      const savedPerm = saved.find(p => samePermissionId(p.id, permission.id))
      return {
        id: permission.id,
        isEnabled: savedPerm?.isEnabled ?? false,
        subPermissions: (permission.subPermissions || []).map(sub => {
          const savedSub = savedPerm?.subPermissions?.find(s =>
            samePermissionId(s.id, sub.id)
          )
          return {
            id: sub.id,
            isEnabled: savedSub?.isEnabled ?? false,
          }
        }),
      }
    }),
  }
}

export const syncSelectionsForModule = (selections, moduleId, enabled) => {
  const categoryIds = getCategoryIdsForModule(moduleId)
  const next = { ...(selections || {}) }
  categoryIds.forEach(categoryId => {
    const key = Number(categoryId)
    if (enabled) {
      const selection = buildCategorySelection(categoryId, true)
      if (selection) {
        next[key] = selection
      } else {
        delete next[key]
      }
    } else {
      delete next[key]
    }
  })
  return next
}

export const setParentInCategorySelection = (selection, permissionId, enabled) => {
  if (!selection) return selection
  return {
    ...selection,
    permissions: selection.permissions.map(permission => {
      if (!samePermissionId(permission.id, permissionId)) return permission
      return {
        ...permission,
        isEnabled: enabled,
        subPermissions: (permission.subPermissions || []).map(sub => ({
          ...sub,
          isEnabled: enabled,
        })),
      }
    }),
  }
}

export const setSubInCategorySelection = (
  selection,
  parentPermissionId,
  subPermissionId,
  enabled
) => {
  if (!selection) return selection
  return {
    ...selection,
    permissions: selection.permissions.map(permission => {
      if (!samePermissionId(permission.id, parentPermissionId)) return permission
      const subPermissions = (permission.subPermissions || []).map(sub =>
        samePermissionId(sub.id, subPermissionId) ? { ...sub, isEnabled: enabled } : sub
      )
      return {
        ...permission,
        isEnabled: permission.isEnabled,
        subPermissions,
      }
    }),
  }
}

export const toggleParentInCategorySelection = (selection, permissionId) => {
  if (!selection) return selection
  const current = selection.permissions.find(p => p.id === permissionId)
  return setParentInCategorySelection(selection, permissionId, !current?.isEnabled)
}

export const toggleSubInCategorySelection = (
  selection,
  parentPermissionId,
  subPermissionId
) => {
  if (!selection) return selection
  const parent = selection.permissions.find(p => p.id === parentPermissionId)
  const sub = parent?.subPermissions?.find(s => s.id === subPermissionId)
  return setSubInCategorySelection(
    selection,
    parentPermissionId,
    subPermissionId,
    !sub?.isEnabled
  )
}

export const toggleCategoryAllInSelection = (selection, selectAll) => {
  if (!selection) return selection
  return {
    ...selection,
    permissions: selection.permissions.map(permission => ({
      ...permission,
      isEnabled: resolvePermissionEnabledForCategory(
        selection.categoryId,
        permission.id,
        selectAll
      ),
      subPermissions: (permission.subPermissions || []).map(sub => ({
        ...sub,
        isEnabled: resolvePermissionEnabledForCategory(
          selection.categoryId,
          permission.id,
          selectAll
        ),
      })),
    })),
  }
}

export const permissionGrantsFromSelections = selections => {
  const keys = []
  Object.entries(selections || {}).forEach(([categoryId, selection]) => {
    if (!selection || !Array.isArray(selection.permissions)) return
    ;(selection.permissions || []).forEach(permission => {
      if (!permission.isEnabled) return
      keys.push(parentGrantKey(categoryId, permission.id))
      ;(permission.subPermissions || [])
        .filter(sub => sub.isEnabled)
        .forEach(sub => {
          keys.push(subGrantKey(categoryId, permission.id, sub.id))
        })
    })
  })
  return sanitizePermissionGrants(grantsFromKeys(keys))
}

export const permissionGrantsFromSelectionsMap = permissionGrantsFromSelections

/**
 * Keep only categories/permission rows allowed by plan.permissionGrants (role Add/Edit UI).
 */
export const filterPermissionCatalogByGrants = (catalog = [], grants = []) => {
  const sanitized = sanitizePermissionGrants(grants)
  if (!sanitized.length) {
    return catalog
  }

  const lookup = new Map()
  sanitized.forEach(entry => {
    const parentMap = new Map()
    ;(entry.permissions || []).forEach(permission => {
      parentMap.set(String(permission.id), new Set(permission.subPermissionIds || []))
    })
    lookup.set(Number(entry.categoryId), parentMap)
  })

  return (catalog || [])
    .filter(group => lookup.has(Number(group?.id)))
    .map(group => {
      const categoryId = Number(group.id)
      const parentMap = lookup.get(categoryId)
      const permissions = (group.permissions || [])
        .filter(perm => parentMap.has(String(perm.id)))
        .filter(perm => !isTenantCountryMutationPermission(categoryId, perm.id))
        .map(perm => {
          const allowedSubs = parentMap.get(String(perm.id))
          const subPermissions = visibleCatalogSubs(
            perm.subPermissions || [],
            allowedSubs
          )
          return { ...perm, subPermissions }
        })

      if (!permissions.length) {
        return null
      }
      return { ...group, permissions }
    })
    .filter(Boolean)
}

/** Align catalog with the logged-in user's plan-scoped role (cannot assign more than you have). */
export const filterPermissionCatalogByRoleScope = (catalog = [], role = null) => {
  if (!role?.permissions?.length) {
    return catalog
  }

  const roleByCategory = new Map(
    role.permissions.map(group => [Number(group?.id), group])
  )

  return (catalog || [])
    .filter(group => roleByCategory.has(Number(group?.id)))
    .map(group => {
      const roleGroup = roleByCategory.get(Number(group.id))
      const rolePermIds = new Set(
        (roleGroup.permissions || []).map(perm => String(perm.id))
      )
      const permissions = (group.permissions || []).filter(perm =>
        rolePermIds.has(String(perm.id))
      )
      if (!permissions.length) {
        return null
      }
      return { ...group, permissions }
    })
    .filter(Boolean)
}
